// /client/src/components/MessageQueueWidget.js
// Dashboard widget showing recent messages with journey status

import React, { useState, useEffect } from 'react';

const MessageQueueWidget = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/messagelogs?limit=10&includeQueued=true', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages((data.messages || []).slice(0, 10));
        setError(null);
      } else {
        setError('Failed to load messages');
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const getMessageTypeLabel = (type) => {
    const labels = {
      'opt-in-request': 'Opt-in Request',
      'opt-in-confirmation': 'Opt-in Confirm',
      'opt-out-confirmation': 'Opt-out Confirm',
      'help-request': 'HELP',
      'help-response': 'Help Response',
      'info-request': 'Info Request',
      'campaign': 'Campaign',
      'pod-relay': 'Pod Relay',
      'test': 'Test',
      'sms': 'SMS',
      'system': 'System'
    };
    return labels[type] || type;
  };

  const isHelpRequest = (msg) => {
    return msg.messageType === 'help-request' || 
           msg.metadata?.isHelpRequest === true ||
           (msg.body && msg.body.trim().toUpperCase() === 'HELP');
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getJourneyStatusColor = (status) => {
    const colors = {
      scheduled: '#3273dc', queued: '#ffdd57', processing: '#ffdd57',
      accepted: '#3298dc', sending: '#3298dc', sent: '#3298dc',
      delivered: '#48c774', undelivered: '#f14668', failed: '#f14668',
      receiving: '#00d1b2', received: '#00d1b2', read: '#48c774',
      cancelled: '#363636'
    };
    return colors[status] || '#888';
  };

  const isTwilioStatus = (status) => {
    return ['accepted', 'sending', 'sent', 'delivered', 'undelivered', 'failed', 'read', 'receiving', 'received'].includes(status);
  };

  // Mini journey timeline for widget
  const MiniJourney = ({ journey }) => {
    if (!journey || journey.length === 0) return <span className="has-text-grey">—</span>;
    
    // Show last 3 statuses
    const recentJourney = journey.slice(-3);
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        {recentJourney.map((step, idx) => {
          const fromTwilio = isTwilioStatus(step.status);
          return (
            <React.Fragment key={idx}>
              <span
                title={`${step.label} (${fromTwilio ? 'Twilio' : 'App'})`}
                style={{
                  display: 'inline-block',
                  padding: '1px 4px',
                  borderRadius: '8px',
                  backgroundColor: getJourneyStatusColor(step.status),
                  color: ['queued', 'processing'].includes(step.status) ? '#333' : '#fff',
                  fontSize: '0.6rem',
                  fontWeight: '500',
                  border: fromTwilio ? '1px solid #f14668' : '1px dashed #3273dc'
                }}
              >
                {step.label?.substring(0, 4)}
              </span>
              {idx < recentJourney.length - 1 && (
                <span style={{ color: '#ccc', fontSize: '0.5rem' }}>›</span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const helpRequestCount = messages.filter(isHelpRequest).length;
  const scheduledCount = messages.filter(m => m.isQueued).length;

  if (loading) {
    return (
      <div className="box">
        <h2 className="subtitle is-5 mb-3">Message Log</h2>
        <p className="has-text-grey">Loading...</p>
      </div>
    );
  }

  return (
    <div className="box boxHeight">
      <div className='mb-1 p-0 is-flex is-justify-content-space-between is-align-items-center' style={{ borderBottom: '1px solid #f0f0f0' }}>
        <div className='mb-3 p-0 is-flex is-align-items-center'>
          <h2 className="subtitle is-5 m-0 p-0 is-bold">Message Log</h2>
          {scheduledCount > 0 && (
            <span className="tag is-link is-rounded ml-2" style={{ fontSize: '0.65rem' }}>
              ⏱️ {scheduledCount}
            </span>
          )}
          {helpRequestCount > 0 && (
            <span 
              className="tag is-danger is-rounded ml-2"
              style={{ animation: 'pulse 2s infinite', fontSize: '0.65rem' }}
              title={`${helpRequestCount} help request(s) need attention`}
            >
              🆘 {helpRequestCount}
            </span>
          )}
        </div>
        <div className='mb-3 p-0'>
          <a href="/messages" className='m-0 p-0 psmall colorBlue'>View more</a>
        </div>
      </div>

      {error && <p className="has-text-danger is-size-7 mb-2">{error}</p>}

      {messages.length === 0 ? (
        <p className="has-text-grey is-size-7">No messages yet</p>
      ) : (
        <div className="table-container">
          <table className="table is-fullwidth is-narrow is-size-7" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ height: '36px' }}>
                <th style={{ width: '40px', verticalAlign: 'middle' }}>Dir</th>
                <th style={{ verticalAlign: 'middle' }}>Contact</th>
                <th style={{ width: '70px', verticalAlign: 'middle' }}>Type</th>
                <th style={{ width: '100px', verticalAlign: 'middle' }}>Journey</th>
                <th style={{ width: '90px', verticalAlign: 'middle' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((msg) => {
                const isHelp = isHelpRequest(msg);
                const isScheduled = msg.isQueued;
                
                const rowStyle = {
                  height: '49px',
                  maxHeight: '49px',
                  backgroundColor: isHelp ? '#fff5f5' : isScheduled ? '#eff5fb' : 'transparent',
                  borderLeft: isHelp ? '3px solid #f14668' : isScheduled ? '3px solid #3273dc' : 'none'
                };
                
                const cellStyle = {
                  verticalAlign: 'middle',
                  padding: '0 8px',
                  height: '49px',
                  maxHeight: '49px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                };
                
                return (
                  <tr 
                    key={msg._id || msg.id}
                    style={rowStyle}
                  >
                    <td style={{ ...cellStyle, paddingLeft: isHelp || isScheduled ? '8px' : undefined }}>
                      <span className={`tag is-small ${msg.direction === 'inbound' ? 'is-info' : 'is-primary'}`}>
                        {msg.direction === 'inbound' ? '↓' : '↑'}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      {msg.contactName || <code style={{ fontSize: '0.65rem' }}>{msg.to?.slice(-4)}</code>}
                    </td>
                    <td style={cellStyle}>
                      {isHelp ? (
                        <span className="tag is-danger is-small" style={{ fontWeight: '600' }}>🆘</span>
                      ) : isScheduled ? (
                        <span className="tag is-link is-small">⏱️</span>
                      ) : (
                        <span style={{ fontSize: '0.65rem' }}>{getMessageTypeLabel(msg.messageType)}</span>
                      )}
                    </td>
                    <td style={{ ...cellStyle, whiteSpace: 'normal' }}>
                      <MiniJourney journey={msg.journey} />
                    </td>
                    <td style={{ ...cellStyle, fontSize: '0.65rem' }}>
                      {isScheduled ? formatTime(msg.scheduledFor) : formatTime(msg.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default MessageQueueWidget;